import type { FetchArgs } from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { anonymizeJson } from '~/features/demoMode/anonymize'

type FetchBaseQueryOptions = Parameters<typeof fetchBaseQuery>[0]

const READ_METHODS = new Set(['GET', 'HEAD'])

/**
 * Demo mode is strictly read-only. Edit flows bind to the pseudonymized models in the
 * RTK Query cache, so a mutation sent mid-demo would overwrite real production customer
 * data with demo fakes. Every generated write endpoint is a `mutation` and every
 * generated `query` endpoint uses GET (verified across all clients), so blocking
 * mutations plus non-GET/HEAD requests blocks all writes without breaking any read.
 */
export function isBlockedDemoModeWrite(args: string | FetchArgs, type: 'query' | 'mutation'): boolean {
  if (type === 'mutation') return true
  const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET')
  return !READ_METHODS.has(method.toUpperCase())
}

export const DEMO_MODE_WRITE_BLOCKED_ERROR = {
  status: 'CUSTOM_ERROR',
  error: 'Demo mode is read-only — exit demo mode to make changes.'
} as const

/**
 * Drop-in replacement for `fetchBaseQuery` that, while demo mode is active:
 *
 * 1. Rejects every write request before it reaches the network (see
 *    {@link isBlockedDemoModeWrite}) so anonymized values can never be saved back to
 *    real customer records.
 * 2. Passes every JSON response body through the demo-mode anonymizer before it enters
 *    the RTK Query cache. Customer-identifying values (names, contact details,
 *    addresses, identifiers, coordinates) are replaced with deterministic pseudonyms;
 *    numeric data is untouched. See `src/features/demoMode/anonymize.ts`.
 *
 * Every API client in this directory must use this wrapper — a client built on a bare
 * `fetchBaseQuery` would leak real customer data into the UI during demos.
 *
 * The demo flag is read structurally from Redux state (same pattern as the org header in
 * `headers.ts`) to avoid a circular import through the store.
 */
export default function anonymizingFetchBaseQuery(options?: FetchBaseQueryOptions): ReturnType<typeof fetchBaseQuery> {
  const baseQuery = fetchBaseQuery(options)

  return async (args, api, extraOptions) => {
    const state = api.getState() as { demoMode?: { enabled?: boolean } }
    const isDemoMode = state.demoMode?.enabled ?? false

    if (isDemoMode && isBlockedDemoModeWrite(args, api.type)) {
      return { error: DEMO_MODE_WRITE_BLOCKED_ERROR }
    }

    const result = await baseQuery(args, api, extraOptions)

    if (isDemoMode) {
      // Both success and error bodies can carry identifying values. Error payloads
      // (parsed backend bodies, error messages) surface in toasts/error UI, so anonymize
      // them too. anonymizeJson preserves structure — `status`, error shape — so RTK error
      // handling still works; only string values are rewritten.
      if (result.data !== undefined) return { ...result, data: anonymizeJson(result.data) }
      if (result.error !== undefined) return { ...result, error: anonymizeJson(result.error) }
    }

    return result
  }
}
